import { Alert, Box, Button, Typography, useTheme } from '@mui/material';
import * as Yup from 'yup';
import { API_GET, API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import ExoForm from '../../components/exo/ExoForm';
import Feedback from '../../components/special/Feedback';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import uuid from 'react-uuid';
import { useDebounce } from 'use-debounce';
import { tokens } from '../../global/theme/tokens';
import { numberToString } from '../../components/special/numberConverter';
import {
  emptyLeg,
  meansOfTransportationOptions,
  parseDecimal,
  toDateTimeInput,
} from '../../components/form/form-fields/TypeTravelExpenseLegs';

// The transport options moved onto the leg field, where they are used. Re-exported
// so the detail views and the preset card keep resolving their labels from here.
export { meansOfTransportationOptions };

const PREVIEW_DEBOUNCE_MS = 400;

function toIso(value) {
  return value ? moment(value).toISOString() : null;
}

// A checkbox value may arrive as the string '0'/'1' from older stored data, and
// the string '0' is truthy, so it must be parsed explicitly.
function parseCheckbox(value) {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'boolean') return value;
  return Boolean(parseInt(value));
}

// A money value from the config may carry a comma decimal, which Number() reads
// as NaN. Returns null when there is no usable value.
function parseConfigNumber(value) {
  const parsed = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

const ADDRESS_KEYS = [
  'street',
  'houseNumber',
  'zipCode',
  'city',
  'country',
  'countryCode',
  'addressAddition',
];

/** Only the address itself: the server snapshots it onto the leg as a new row. */
function serializeAddress(address) {
  if (!address?.street) return null;
  return Object.fromEntries(
    ADDRESS_KEYS.filter((key) => address[key] != null).map((key) => [key, address[key]]),
  );
}

function serializeLeg(leg, index) {
  return {
    ...(leg.id ? { id: leg.id } : {}),
    ...(leg.travelExpensePresetId
      ? { travelExpensePresetId: leg.travelExpensePresetId }
      : {}),
    ...(serializeAddress(leg.toAddress)
      ? { toAddress: serializeAddress(leg.toAddress) }
      : {}),
    sortOrder: index,
    fromLocation: leg.fromLocation || null,
    toLocation: leg.toLocation || null,
    departedAt: toIso(leg.departedAt),
    arrivedAt: toIso(leg.arrivedAt),
    meansOfTransportation: leg.meansOfTransportation,
    distanceKilometers: leg.distanceKilometers
      ? parseDecimal(leg.distanceKilometers)
      : null,
    // An explicit 0 (train, bus, … — no km rate) must survive as 0: the server
    // fills its configured default in only when the rate is missing entirely.
    pricePerKilometer:
      leg.pricePerKilometer === '' || leg.pricePerKilometer == null
        ? null
        : parseDecimal(leg.pricePerKilometer) || 0,
    isReturnLeg: Boolean(leg.isReturnLeg),
  };
}

/** An abandoned empty row: nothing entered, so validation skips it and it is
 *  never sent (serializeLegs already strips legs without a departure). */
function isBlankLeg(leg) {
  return !(
    leg.fromLocation ||
    leg.toLocation ||
    leg.departedAt ||
    leg.arrivedAt ||
    parseDecimal(leg.distanceKilometers) > 0
  );
}

/** For saving: only complete legs. A saved leg needs at least a departure. */
function serializeLegs(legs = []) {
  return legs.filter((leg) => leg.departedAt).map(serializeLeg);
}

/**
 * For the live preview: a leg being typed counts as soon as it has any
 * substance, so the running total moves with the first distance entered rather
 * than waiting for the departure time.
 */
function serializePreviewLegs(legs = []) {
  return legs
    .filter((leg) => leg.departedAt || parseDecimal(leg.distanceKilometers) > 0)
    .map(serializeLeg);
}

function serializeCost(cost) {
  return {
    ...(cost.id ? { id: cost.id } : {}),
    category: cost.category,
    incurredOn: cost.incurredOn || null,
    description: cost.description || null,
    grossAmount: Number(cost.grossAmount) || 0,
    taxRate: parseDecimal(cost.taxRate) || 0,
    fileId: cost.fileId || null,
  };
}

/** For saving: only complete costs. A saved cost needs its date. */
function serializeCosts(costs = []) {
  return costs
    .filter((cost) => cost.incurredOn && cost.grossAmount)
    .map(serializeCost);
}

/** For the live preview: an amount counts as soon as it is typed, date or not. */
function serializePreviewCosts(costs = []) {
  return costs.filter((cost) => Number(cost.grossAmount) > 0).map(serializeCost);
}

function serializeDays(days = []) {
  return days
    .filter((day) => day.date)
    .map((day) => ({
      date: day.date,
      rateType: day.rateType || 'full',
      breakfastProvided: Boolean(day.breakfastProvided),
      lunchProvided: Boolean(day.lunchProvided),
      dinnerProvided: Boolean(day.dinnerProvided),
      overnightFlatRateClaimed: Boolean(day.overnightFlatRateClaimed),
    }));
}

/** A server-suggested day carries only a date and a rate type; the meal answers
 *  start empty for the employee to fill in. */
function suggestionToFormDay(day) {
  return {
    uuid: uuid(),
    date: day.date,
    rateType: day.rateType || 'full',
    breakfastProvided: false,
    lunchProvided: false,
    dinnerProvided: false,
    overnightFlatRateClaimed: false,
  };
}

/** The travel log for an existing trip, or a single row seeded from a preset. */
function initialLegs(travelExpense, startData) {
  if (travelExpense?.legs?.length) {
    return travelExpense.legs.map((leg) => ({
      id: leg.id,
      fromLocation: leg.fromLocation || '',
      toLocation: leg.toLocation || '',
      departedAt: toDateTimeInput(leg.departedAt),
      arrivedAt: toDateTimeInput(leg.arrivedAt),
      meansOfTransportation: leg.meansOfTransportation || 'private-car',
      distanceKilometers: leg.distanceKilometers ?? '',
      pricePerKilometer: leg.pricePerKilometer ?? '',
      isReturnLeg: Boolean(leg.isReturnLeg),
    }));
  }

  // The price is left empty here: the config is not available yet on the very
  // first render, and the effect below fills the configured rate in as soon as
  // it is (see travel_expense_default_price_per_kilometer).
  const seeded = emptyLeg();

  // Seeded from a project's saved destination (Anfahrt). The preset reference and
  // its address ride on the leg, which is what the preset actually describes.
  if (startData?.distanceKilometers) {
    seeded.distanceKilometers = startData.distanceKilometers;
    seeded.pricePerKilometer = startData.pricePerKilometer ?? '';
    seeded.travelExpensePresetId = startData.travelExpensePresetId ?? undefined;
    seeded.toAddress = startData.destinationAddress ?? undefined;
    seeded.fromLocation = startData.fromLocation || '';
    seeded.toLocation =
      startData.toLocation || startData.destinationAddress?.city || '';
  }

  return [seeded];
}

/**
 * The travel expense form.
 *
 * Layout (design "1a"): a single content column of cards — title + project in
 * one header row, the travel log as a dense table, the meal allowance with its
 * overnight checkbox in the card header, additional costs and comment side by
 * side — next to a sticky summary sidebar holding the cost breakdown, the
 * running total, validation warnings and the Cancel/Save actions. On mobile
 * everything stacks and the summary comes last.
 *
 * The form itself is an ExoForm with the yup schema below; the cards are
 * `group` fields (card variant). ExoForm owns the data collection — this
 * component mirrors it via onChange (for the server preview) and reads it back
 * through the ref on save.
 */
const NewTravelExpense = ({
  onCreate,
  onCancel,
  userId,
  startData,
  // Pass an existing travel expense to edit it. The trip is a first-class record,
  // so it is patched in place rather than being declined and recreated.
  travelExpense,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const isEdit = Boolean(travelExpense?.id);
  const currencySymbol = config?.currency?.currencySymbol ?? '';
  // travel_expense_default_price_per_kilometer, served by the config endpoint
  // in the "Travel Expense" category.
  const defaultPricePerKilometer =
    parseConfigNumber(
      config?.['travel expense']?.travelExpenseDefaultPricePerKilometer,
    ) ?? 0.3;
  // travel_expense_allow_costs_without_receipt: when disabled, the missing
  // receipt stops being a soft warning and becomes a validation error.
  const allowCostsWithoutReceipt =
    parseCheckbox(
      config?.['travel expense']?.travelExpenseAllowCostsWithoutReceipt,
    ) ?? true;

  const formRef = useRef(null);

  // Built once; ExoForm merges it into its data collection on mount.
  const [startDataCollection] = useState(() => ({
    project: travelExpense?.project ?? startData?.project ?? null,
    // Left empty on new trips: the server books the trip on the project's
    // "Allgemeine Kosten" cost center (or "Vertrieb") unless one is chosen.
    costCenter: travelExpense?.costCenter ?? null,
    comment: travelExpense?.comment ?? '',
    legs: initialLegs(travelExpense, startData),
    // The uuid is the day row's render key; serializeDays strips it again.
    days: travelExpense?.days
      ? serializeDays(travelExpense.days).map((day) => ({ ...day, uuid: uuid() }))
      : [],
    costs: travelExpense?.costs
      ? travelExpense.costs.map((cost) => ({
          id: cost.id,
          category: cost.category,
          incurredOn: cost.incurredOn,
          description: cost.description || '',
          grossAmount: cost.grossAmount,
          taxRate: cost.taxRate,
          fileId: cost.fileId,
          fileName: cost.file?.title || '',
          file: cost.file || null,
        }))
      : [],
    hasOvernightStay: parseCheckbox(travelExpense?.hasOvernightStay),
  }));

  const [preview, setPreview] = useState(null);
  const previewRequestId = useRef(0);
  // The day set follows the travel log automatically for as long as the employee
  // has not touched it: editing the departure or arrival re-derives the days. The
  // first manual day edit hands the set over to the employee for good, so their
  // choices are never overwritten. Editing an existing trip starts handed over.
  const daysTouched = useRef(isEdit);
  // Re-derive only when the suggestion's inputs actually changed, otherwise the
  // suggestion writing its days back would trigger itself forever.
  const lastSuggestKey = useRef(null);
  // The exact day array the suggestion last wrote into the form, so its own
  // write-back is recognized and does not count as the employee touching them.
  const suggestedDays = useRef(null);
  const lastSeenDays = useRef(startDataCollection.days);

  // ExoForm owns the data collection; this mirror feeds the live preview and
  // the preset fetch. Change detection is by identity: the day editor commits a
  // new array on every employee edit.
  const [formData, setFormData] = useState(null);

  function handleChange(data) {
    if (
      data.days &&
      data.days !== lastSeenDays.current &&
      data.days !== suggestedDays.current
    ) {
      daysTouched.current = true;
    }
    if (data.days) lastSeenDays.current = data.days;

    setFormData(data);
  }

  // useConfig only delivers the config after a component's first render, so the
  // leg seeded at mount cannot know the configured rate yet. Fill it in when the
  // config arrives — only into legs whose price is still empty, never over a
  // value the employee (or an existing trip) already has.
  const priceFilled = useRef(false);
  useEffect(() => {
    if (!config || priceFilled.current) return;

    const legs = formRef.current?.dataCollection?.legs;
    if (!legs?.length) return;

    priceFilled.current = true;
    formRef.current.updateDataCollection({
      legs: legs.map((leg) =>
        leg.pricePerKilometer === '' || leg.pricePerKilometer == null
          ? {
              ...leg,
              // Only the private car earns the km rate; other transports ride
              // on their receipts and get an explicit 0.
              pricePerKilometer:
                (leg.meansOfTransportation || 'private-car') === 'private-car'
                  ? defaultPricePerKilometer
                  : 0,
            }
          : leg,
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, formData]);

  // Saved routes for the selected project, feeding the travel log's
  // "From Preset" picker. undefined while no project is picked keeps the
  // button disabled with a hint instead of hiding it.
  const [projectPresets, setProjectPresets] = useState(undefined);
  const projectId = formData?.project?.id;

  useEffect(() => {
    if (!config || !projectId) {
      setProjectPresets(undefined);
      return;
    }

    let stale = false;
    DBRequest({
      config,
      path: `travel-expense-presets?projectId[eq]=${projectId}&perPage=100`,
      method: API_GET,
      onResponse: (data) => {
        if (!stale) setProjectPresets(data.data || []);
      },
    });

    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, projectId]);

  // --- validation ----------------------------------------------------------

  const validationSchema = Yup.object().shape({
    project: Yup.object()
      .nullable()
      .test('project-selected', t('Project is required'), (project) =>
        Boolean(project?.id),
      ),
    legs: Yup.array()
      .of(
        Yup.object().shape({
          // A fully-empty row is an abandoned row, not a mistake: like the
          // costs below, it is skipped here and stripped on save.
          departedAt: Yup.string().test(
            'departure-required',
            t('Every leg needs a departure time'),
            (departedAt, ctx) => Boolean(departedAt) || isBlankLeg(ctx.parent),
          ),
          meansOfTransportation: Yup.string().required(
            t('Means of Transportation is required'),
          ),
          arrivedAt: Yup.string()
            .nullable()
            .test(
              'arrival-after-departure',
              t('Arrival must be after departure'),
              (arrivedAt, ctx) =>
                !arrivedAt ||
                !ctx.parent.departedAt ||
                moment(arrivedAt).isSameOrAfter(moment(ctx.parent.departedAt)),
            ),
        }),
      )
      .test('at-least-one-leg', t('At least one leg is required'), (legs = []) =>
        legs.some((leg) => !isBlankLeg(leg)),
      ),
    // Only costs that would actually be saved need their receipt — an
    // abandoned empty row must not block the request.
    ...(allowCostsWithoutReceipt
      ? {}
      : {
          costs: Yup.array().test(
            'receipts-required',
            t('Every additional cost needs a receipt'),
            (costs = []) =>
              costs.every(
                (cost) => !(cost.incurredOn && cost.grossAmount) || cost.fileId,
              ),
          ),
        }),
  });

  // Errors are computed live by the form but only shown after a failed save
  // attempt, so the form does not open covered in red.
  const [showErrors, setShowErrors] = useState(false);

  /** The messages for the sidebar's warning box, leg errors prefixed with their
   *  row number so "Every leg needs a departure time" points at a row. */
  function collectErrorMessages(data) {
    try {
      validationSchema.validateSync(data, { abortEarly: false });
      return [];
    } catch (err) {
      return [
        ...new Set(
          (err.inner || []).map((e) => {
            const legMatch = /^legs\[(\d+)\]/.exec(e.path || '');
            return legMatch
              ? `${t('Leg')} ${Number(legMatch[1]) + 1}: ${e.message}`
              : e.message;
          }),
        ),
      ];
    }
  }

  // --- server preview ------------------------------------------------------

  /**
   * Ask the server to propose the day set from the travel log and adopt it into
   * the form. Runs once on first use and again on "regenerate from travel log".
   */
  const suggestDays = useCallback(
    (data) => {
      const legs = serializeLegs(data.legs);
      if (!legs.length) return;

      const hasOvernightStay = parseCheckbox(data.hasOvernightStay);
      const requestId = ++previewRequestId.current;

      DBRequest({
        config,
        path: 'travel-expenses/preview',
        method: API_POST({
          legs,
          suggestDays: true,
          costs: serializePreviewCosts(data.costs),
          ...(hasOvernightStay === undefined ? {} : { hasOvernightStay }),
        }),
        onResponse: (response) => {
          if (requestId !== previewRequestId.current) return;
          // The employee may have taken the days over while the suggestion was
          // in flight; their edit wins and the next preview prices it.
          if (daysTouched.current) return;
          setPreview(response.data);
          // Remembered as the suggestion's own write, so adopting it does not
          // count as the employee touching the days.
          const days = (response.data.days || []).map(suggestionToFormDay);
          suggestedDays.current = days;
          formRef.current?.updateDataCollection({ days });
        },
      });
    },
    [config],
  );

  /**
   * Price whatever the form currently holds. The meal allowance rules are
   * timezone- and edge-case-heavy, so the server is the single source of truth
   * and the form only renders what it returns.
   */
  const priceCurrent = useCallback(
    (data) => {
      const previewLegs = serializePreviewLegs(data.legs);
      const previewCosts = serializePreviewCosts(data.costs);

      if (
        !previewLegs.length &&
        !previewCosts.length &&
        !serializeDays(data.days).length
      ) {
        return setPreview(null);
      }

      // While the employee has not touched the days, they follow the travel log:
      // any change to a timespan re-derives them. The suggestion needs times, so
      // it waits for a complete leg even while incomplete ones are being priced.
      const strictLegs = serializeLegs(data.legs);
      const suggestKey = JSON.stringify([
        strictLegs.map((leg) => [leg.departedAt, leg.arrivedAt]),
        parseCheckbox(data.hasOvernightStay) ?? null,
      ]);

      if (
        !daysTouched.current &&
        strictLegs.length &&
        suggestKey !== lastSuggestKey.current
      ) {
        lastSuggestKey.current = suggestKey;
        return suggestDays(data);
      }

      const requestId = ++previewRequestId.current;

      DBRequest({
        config,
        path: 'travel-expenses/preview',
        method: API_POST({
          legs: previewLegs,
          days: serializeDays(data.days),
          costs: previewCosts,
        }),
        onResponse: (response) => {
          // Responses can arrive out of order; only the latest request may win,
          // or a slow stale reply would overwrite fresher totals.
          if (requestId === previewRequestId.current) setPreview(response.data);
        },
      });
    },
    [config, suggestDays],
  );

  const [debouncedFormData] = useDebounce(formData, PREVIEW_DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedFormData) priceCurrent(debouncedFormData);
  }, [debouncedFormData, priceCurrent]);

  // --- submit --------------------------------------------------------------

  const [submitting, setSubmitting] = useState(false);

  function handleSave() {
    const form = formRef.current;
    const values = form?.dataCollection || {};

    // Checked synchronously on top of the form's async validity: the receipt
    // requirement must hold at the moment of the click, not at the last
    // validation run. Only costs that would actually be saved count.
    const receiptRequiredButMissing =
      !allowCostsWithoutReceipt &&
      (values.costs || []).some(
        (cost) => cost.incurredOn && cost.grossAmount && !cost.fileId,
      );

    if (!form?.isValid() || receiptRequiredButMissing) {
      setShowErrors(true);
      handleAlertOpen(t('Please check the highlighted fields'), 'warning');
      return;
    }
    const payload = {
      projectId: values.project?.id,
      costCenterId: values.costCenter?.id ?? null,
      comment: values.comment || null,
      hasOvernightStay: parseCheckbox(values.hasOvernightStay) ?? null,
      legs: serializeLegs(values.legs),
      days: serializeDays(values.days),
      costs: serializeCosts(values.costs),
    };

    setSubmitting(true);

    if (isEdit) {
      return DBRequest({
        config,
        path: `travel-expenses/${travelExpense.id}`,
        method: API_PATCH(payload),
        onResponse: handleDone,
        onFeedback: handleAlertOpen,
        onError: () => setSubmitting(false),
      });
    }

    DBRequest({
      config,
      path: 'employeerequests/travel-expense',
      method: API_POST({
        type: 'travelExpense',
        comment: values.comment,
        values: JSON.stringify(payload),
        ...(userId ? { userId } : {}),
      }),
      onResponse: handleDone,
      onFeedback: handleAlertOpen,
      onError: () => setSubmitting(false),
    });
  }

  function handleDone(data) {
    setSubmitting(false);
    onCreate(data.data);
    onCancel();
  }

  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  // --- render --------------------------------------------------------------

  const money = (value) => `${numberToString(value ?? 0)} ${currencySymbol}`;

  const fields = [
    // Header row: title and project share one line, so the form starts with
    // content instead of chrome.
    {
      type: 'group',
      key: 'headerRow',
      className: 'flex items-center gap-4 w-full',
      errorsVisible: showErrors,
      fields: [
        {
          type: 'heading',
          label: isEdit ? t('Edit Travel Expense') : t('New Travel Expense'),
        },
        { label: t('Project'), type: 'project', key: 'project', apiUrl: 'projects' },
      ],
    },
    {
      type: 'group',
      variant: 'card',
      key: 'legsCard',
      label: t('Travel Log'),
      badge: money(preview?.mileageTotal),
      className: 'flex flex-col',
      errorsVisible: showErrors,
      fields: [
        {
          type: 'travelExpenseLegs',
          key: 'legs',
          currencySymbol,
          defaultPricePerKilometer,
          presets: projectPresets,
        },
      ],
    },
    {
      type: 'group',
      variant: 'card',
      key: 'daysCard',
      label: t('Meal allowance'),
      badge: money((preview?.perDiemTotal ?? 0) + (preview?.overnightTotal ?? 0)),
      className: 'flex flex-col',
      errorsVisible: showErrors,
      headerFields: [
        {
          label: t('Overnight stay away from home'),
          type: 'infoCheckbox',
          key: 'hasOvernightStay',
          tooltip: t(
            'Decides whether the arrival and departure day each earn the partial rate. Without an overnight stay a trip crossing midnight counts as a single day.',
          ),
        },
      ],
      fields: [{ type: 'travelExpenseDays', key: 'days', currencySymbol, preview }],
    },
    {
      type: 'group',
      key: 'bottomRow',
      className: 'grid grid-cols-1 xl:grid-cols-2 gap-5 w-full items-start',
      errorsVisible: showErrors,
      fields: [
        {
          type: 'group',
          variant: 'card',
          key: 'costsCard',
          label: t('Additional costs'),
          badge: money(preview?.costsTotal),
          fields: [{ type: 'travelExpenseCosts', key: 'costs', currencySymbol }],
        },
        {
          type: 'group',
          variant: 'card',
          key: 'commentCard',
          label: t('Comment'),
          fields: [
            {
              type: 'text',
              key: 'comment',
              multiline: true,
              minRows: 2,
              autoCorrect: true,
              placeholder: t('Optional'),
            },
          ],
        },
        {
          type: 'group',
          variant: 'card',
          key: 'costCenterCard',
          label: t('Cost Center'),
          fields: [
            {
              type: 'costCenter',
              key: 'costCenter',
              label: t('Cost Center'),
            },
          ],
        },
      ],
    },
  ];

  const data = formData ?? startDataCollection;
  const legsCount = (data.legs || []).length;
  const daysCount = (data.days || []).length;

  const summaryRows = [
    {
      label: `${t('Mileage allowance')} · ${legsCount} ${t(legsCount === 1 ? 'Leg' : 'Legs')}`,
      value: preview?.mileageTotal,
    },
    {
      label: `${t('Meal allowance')} · ${daysCount} ${t(daysCount === 1 ? 'Day' : 'Days')}`,
      value: preview?.perDiemTotal,
    },
    ...((preview?.overnightTotal ?? 0) > 0
      ? [{ label: t('Accommodation allowance'), value: preview?.overnightTotal }]
      : []),
    { label: t('Additional costs'), value: preview?.costsTotal },
  ];

  const errorMessages = showErrors ? collectErrorMessages(data) : [];

  // A receipt is not required to save, so this is a standing soft warning
  // rather than a validation error: reimbursement without a receipt is what
  // the reviewer will bounce.
  const missingReceipts = (data.costs || []).filter(
    (cost) => !cost.fileId,
  ).length;

  return (
    <Box className="h-full overflow-y-auto px-1 py-2">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 300px' },
          gap: '24px',
          alignItems: 'start',
        }}
      >
        <ExoForm
          ref={formRef}
          noControls
          noDifferences
          fields={fields}
          startDataCollection={startDataCollection}
          validationSchema={validationSchema}
          onChange={handleChange}
          outerClassName="flex flex-col min-w-0"
          className="flex flex-col gap-5"
        />

        {/* Summary sidebar: breakdown, total, warnings and the actions. Sticky
            next to the content on desktop, last card on mobile. */}
        <Box
          className="flex flex-col gap-3 p-4 rounded-xl"
          sx={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.selected}`,
            position: { xs: 'static', lg: 'sticky' },
            top: 0,
          }}
        >
          <Typography
            variant="caption"
            className="uppercase opacity-60"
            sx={{ fontWeight: 600, letterSpacing: '0.06em' }}
          >
            {t('Summary')}
          </Typography>

          {summaryRows.map((row) => (
            <Box key={row.label} className="flex justify-between items-baseline gap-2">
              <Typography variant="body2" className="opacity-60">
                {row.label}
              </Typography>
              <Typography variant="body2" className="monospace whitespace-nowrap">
                {money(row.value)}
              </Typography>
            </Box>
          ))}

          <Box
            className="flex justify-between items-baseline gap-2 pt-3"
            sx={{ borderTop: `1px solid ${colors.selected}` }}
          >
            <Typography sx={{ fontWeight: 600 }}>{t('Total')}</Typography>
            <Typography variant="h4" className="monospace whitespace-nowrap">
              {money(preview?.totalPrice)}
            </Typography>
          </Box>

          {/* Something app-global stacks alert icon and text vertically; a
              plain sx class loses to it, so the row rule needs the doubled
              specificity (same reason the NewVacation fix goes through a
              parent selector). */}
          {errorMessages.length > 0 && (
            <Alert
              severity="warning"
              sx={{
                '&.MuiAlert-root': { flexDirection: 'row', alignItems: 'center' },
              }}
            >
              {errorMessages.map((message) => (
                <Typography key={message} variant="body2">
                  {message}
                </Typography>
              ))}
            </Alert>
          )}

          {missingReceipts > 0 && (
            <Alert
              severity={allowCostsWithoutReceipt ? 'warning' : 'error'}
              sx={{
                '&.MuiAlert-root': { flexDirection: 'row', alignItems: 'center' },
              }}
            >
              {missingReceipts === 1
                ? t('Receipt missing')
                : `${missingReceipts} ${t('Receipts missing')}`}
            </Alert>
          )}

          <Button
            variant="contained"
            color="success"
            disabled={submitting}
            onClick={handleSave}
            sx={{ ':active': { transform: 'scale(0.97)' } }}
          >
            {t('Save')}
          </Button>
          {onCancel && (
            <Button variant="outlined" color="error" onClick={onCancel}>
              {t('Cancel')}
            </Button>
          )}
        </Box>
      </Box>

      <Feedback setState={setAlertState} state={alertState} />
    </Box>
  );
};

export default NewTravelExpense;
